/**
 * ORDER TRANSACTIONS — Operações atômicas com Firebase
 *
 * - Evita race conditions (dois motoboys aceitando o mesmo pedido)
 * - Operações atômicas (tudo ou nada)
 *
 * FIX v1.1.0: leituras antes de escritas na transaction
 * FIX v1.2.0: fila no_driver/expired → ORDER_EXPIRED; mapear permission-denied
 */

import { doc, runTransaction, serverTimestamp, type DocumentSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebaseConfig';
import { Order, OrderStatus } from '../types';
import { assertCanDispatchOrder } from '../utils/paymentPolicy';

export type AcceptOrderError =
  | 'ALREADY_ACCEPTED'
  | 'ORDER_NOT_FOUND'
  | 'INVALID_STATUS'
  | 'ORDER_EXPIRED'
  | 'PERMISSION_DENIED'
  | 'UNKNOWN';

export interface AcceptOrderResult {
  success: boolean;
  error?: AcceptOrderError;
  order?: Order;
  acceptedBy?: string;
}

function mapAcceptCatchError(error: unknown): AcceptOrderError {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: string }).code || '')
      : '';
  if (code === 'permission-denied') return 'PERMISSION_DENIED';
  return 'UNKNOWN';
}

/**
 * Motoboy aceita pedido de forma atômica.
 */
export async function acceptOrderTransaction(
  orderId: string,
  driverId: string,
  driverName?: string
): Promise<AcceptOrderResult> {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const queueRef = doc(db, 'delivery_queue', orderId);

    const result = await runTransaction(db, async (transaction) => {
      const orderDoc = await transaction.get(orderRef);

      if (!orderDoc.exists()) {
        return { success: false, error: 'ORDER_NOT_FOUND' as const };
      }

      const orderData = orderDoc.data() as Order;

      if (orderData.driverId === driverId && orderData.status === 'delivering') {
        return { success: true, order: orderData as Order };
      }

      if (orderData.driverId && orderData.driverId !== driverId) {
        return {
          success: false,
          error: 'ALREADY_ACCEPTED' as const,
          acceptedBy: orderData.driverId,
        };
      }

      if (orderData.status !== 'ready_for_pickup') {
        return { success: false, error: 'INVALID_STATUS' as const };
      }

      if ((orderData as { offerExpired?: boolean }).offerExpired === true) {
        return { success: false, error: 'ORDER_EXPIRED' as const };
      }

      // Pix não confirmado: pedido indisponível para o motoboy (DriverDashboard já trata ORDER_EXPIRED).
      if (!assertCanDispatchOrder(orderData).ok) {
        return { success: false, error: 'ORDER_EXPIRED' as const };
      }

      const noDriverAssigned = !orderData.driverId || orderData.driverId === '';
      if (!noDriverAssigned) {
        return {
          success: false,
          error: 'ALREADY_ACCEPTED' as const,
          acceptedBy: String(orderData.driverId),
        };
      }

      // Lalamove: corrida já é gerenciada pela plataforma externa — motoboy do pool NUNCA aceita.
      if (orderData.lalamoveOrderId || orderData.deliveryProvider === 'lalamove') {
        return { success: false, error: 'ORDER_EXPIRED' as const };
      }

      // delivery_queue: status expired/cancelled/lalamove → não aceitar.
      // no_driver é aceitável: o pedido segue em ready_for_pickup no painel e pode ser aceito.
      let queueDoc: DocumentSnapshot | null = null;
      try {
        queueDoc = await transaction.get(queueRef);
      } catch (queueReadErr) {
        console.warn('[AcceptOrder] leitura delivery_queue falhou', orderId, queueReadErr);
        return { success: false, error: 'ORDER_EXPIRED' as const };
      }

      if (queueDoc.exists()) {
        const qd = queueDoc.data() as Record<string, unknown>;
        const qDriver = qd.driverId != null ? String(qd.driverId).trim() : '';
        const qStatus = typeof qd.status === 'string' ? qd.status : '';

        if (qStatus === 'expired' || qStatus === 'cancelled' || qStatus === 'lalamove') {
          return { success: false, error: 'ORDER_EXPIRED' as const };
        }

        if (qDriver && qDriver !== driverId) {
          return {
            success: false,
            error: 'ALREADY_ACCEPTED' as const,
            acceptedBy: qDriver,
          };
        }

        if (qStatus && qStatus !== 'waiting') {
          const recoverSelf =
            (qStatus === 'accepted' || qStatus === 'assigned') &&
            qDriver === driverId &&
            noDriverAssigned;
          if (!recoverSelf) {
            return { success: false, error: 'ORDER_EXPIRED' as const };
          }
        }
      }

      const updatedOrder: Partial<Order> = {
        driverId,
        driverUid: driverId,
        status: 'delivering' as OrderStatus,
        acceptedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      transaction.update(orderRef, {
        ...updatedOrder,
        _serverTimestamp: serverTimestamp(),
      });

      if (queueDoc?.exists()) {
        const qd = queueDoc.data() as Record<string, unknown>;
        const qStatus = typeof qd.status === 'string' ? qd.status : '';
        if (qStatus === 'waiting' || qStatus === 'no_driver' || qStatus === 'accepted' || qStatus === 'assigned') {
          transaction.update(queueRef, {
            driverId,
            driverName: driverName || 'Entregador',
            status: 'accepted',
            acceptedAt: new Date().toISOString(),
          });
        }
      }

      return {
        success: true,
        order: { ...orderData, ...updatedOrder } as Order,
      };
    });

    return result;
  } catch (error) {
    console.error(`[Transaction] Erro ao aceitar pedido ${orderId}:`, error);
    return { success: false, error: mapAcceptCatchError(error) };
  }
}

export interface CompleteOrderResult {
  success: boolean;
  error?: 'ORDER_NOT_FOUND' | 'NOT_YOUR_ORDER' | 'INVALID_STATUS' | 'PRECONDITION' | 'UNKNOWN';
  errorMessage?: string;
  order?: Order;
}

export async function completeOrderTransaction(
  orderId: string,
  driverId: string
): Promise<CompleteOrderResult> {
  try {
    const callable = httpsCallable<{ orderId: string }, { ok?: boolean; legacy?: boolean }>(
      getFunctions(undefined, 'us-central1'),
      'completeDelivery'
    );
    const response = await callable({ orderId });
    if (response.data?.ok === true || response.data?.legacy === true) {
      return { success: true };
    }
    return { success: false, error: 'UNKNOWN', errorMessage: 'Resposta inválida do servidor.' };
  } catch (error) {
    console.error(`[Transaction] Erro ao concluir entrega ${orderId}:`, error);
    const rawCode = error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: unknown }).code || '') : '';
    let mapped: CompleteOrderResult['error'] = 'UNKNOWN';
    if (rawCode === 'functions/permission-denied') mapped = 'NOT_YOUR_ORDER';
    else if (rawCode === 'functions/not-found') mapped = 'ORDER_NOT_FOUND';
    else if (rawCode === 'functions/failed-precondition') mapped = 'PRECONDITION';

    let message = error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message || '') : '';
    if (message === 'lalamove_managed') {
      message = 'Esta entrega é gerenciada pela Lalamove.';
    }

    return { success: false, error: mapped, errorMessage: message || 'Tente novamente em instantes.' };
  }
}

export interface UpdateOrderStatusResult {
  success: boolean;
  error?: 'ORDER_NOT_FOUND' | 'INVALID_TRANSITION' | 'UNKNOWN';
  order?: Order;
}

export async function updateOrderStatusTransaction(
  orderId: string,
  newStatus: OrderStatus,
  _vendorId?: string
): Promise<UpdateOrderStatusResult> {
  const validTransitions: Record<string, OrderStatus[]> = {
    pending: ['confirmed', 'preparing', 'cancelled'],
    confirmed: ['preparing', 'cancelled'],
    preparing: ['ready', 'ready_for_pickup', 'cancelled'],
    ready: ['ready_for_pickup', 'delivered', 'cancelled'],
    ready_for_pickup: ['delivering', 'cancelled'],
    delivering: ['delivered', 'cancelled'],
    delivered: ['completed'],
    completed: [],
    cancelled: [],
  };

  try {
    const orderRef = doc(db, 'orders', orderId);

    const result = await runTransaction(db, async (transaction) => {
      const orderDoc = await transaction.get(orderRef);

      if (!orderDoc.exists()) {
        return { success: false, error: 'ORDER_NOT_FOUND' as const };
      }

      const orderData = orderDoc.data() as Order;
      const currentStatus = orderData.status;

      const allowedStatuses = validTransitions[currentStatus] || [];
      if (!allowedStatuses.includes(newStatus)) {
        return { success: false, error: 'INVALID_TRANSITION' as const };
      }

      const updatedOrder: Partial<Order> = {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      };

      transaction.update(orderRef, {
        ...updatedOrder,
        _serverTimestamp: serverTimestamp(),
      });

      return {
        success: true,
        order: { ...orderData, ...updatedOrder } as Order,
      };
    });

    return result;
  } catch (error) {
    console.error(`[Transaction] Erro ao atualizar pedido ${orderId}:`, error);
    return { success: false, error: 'UNKNOWN' };
  }
}
